import { create } from 'zustand';

const LEGACY_KEY = 'pointcraft-payment-recovery-blocks-v5';
const KEY = 'pointcraft-payment-recovery-issues-v1';

export type RecoveryIssueCode =
  | 'STORAGE_UNREADABLE'
  | 'MALFORMED_RECORD'
  | 'UNSUPPORTED_FORMAT'
  | 'MIGRATION_WRITE_FAILED'
  | 'IDENTITY_CONFLICT'
  | 'MEMORY_ONLY'
  | 'PENDING'
  | 'READY_TO_RESUME';

export interface PaymentRecoveryIssue {
  id: string;
  businessId: string | null;
  message: string;
  code: RecoveryIssueCode;
  /** Stable outcome, not retry count, timestamp, or transport error text. */
  revision: string;
}
interface IssueRegistry {
  issues: PaymentRecoveryIssue[];
  dismissed: Record<string, string>;
}

function readRegistry(): IssueRegistry {
  try {
    const current = localStorage.getItem(KEY);
    const parsed = current ? JSON.parse(current) : null;
    const records = parsed?.issues ?? JSON.parse(localStorage.getItem(LEGACY_KEY) ?? '[]');
    if (!Array.isArray(records)) throw new Error('Invalid registry');
    const issues = records.map((record): PaymentRecoveryIssue => {
      if (
        !record ||
        typeof record.id !== 'string' ||
        typeof record.message !== 'string' ||
        (record.code !== undefined && typeof record.code !== 'string') ||
        (record.revision !== undefined && typeof record.revision !== 'string') ||
        (record.businessId !== null && typeof record.businessId !== 'string')
      )
        throw new Error('Invalid issue');
      return {
        ...record,
        code: record.code ?? 'PENDING',
        revision: record.revision ?? 'PENDING',
        // Old register-blocking copy is never shown after upgrading.
        message: record.code
          ? record.message
          : 'An earlier payment needs review. You can continue with new sales.',
      };
    });
    const dismissed = parsed?.dismissed;
    return {
      issues,
      dismissed:
        dismissed && typeof dismissed === 'object' && !Array.isArray(dismissed) ? dismissed : {},
    };
  } catch {
    return {
      issues: [
        {
          id: 'registry-unreadable',
          businessId: null,
          code: 'STORAGE_UNREADABLE',
          revision: 'STORAGE_UNREADABLE',
          message:
            'Payment recovery notices could not be read. Existing payment data is retained. You can continue with new sales.',
        },
      ],
      dismissed: {},
    };
  }
}

export const usePaymentRecoveryIssues = create<IssueRegistry>(readRegistry);

function persistRegistry(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(usePaymentRecoveryIssues.getState()));
  } catch {
    // Presentation storage must never throw into financial work or dismissal.
  }
}

export function reportPaymentRecoveryIssue(
  input: Omit<PaymentRecoveryIssue, 'code' | 'revision'> &
    Partial<Pick<PaymentRecoveryIssue, 'code' | 'revision'>>
): void {
  const issue = {
    ...input,
    code: input.code ?? 'PENDING',
    revision: input.revision ?? input.code ?? 'PENDING',
  };
  const state = usePaymentRecoveryIssues.getState();
  const previous = state.issues.find((entry) => entry.id === issue.id);
  if (previous && JSON.stringify(previous) === JSON.stringify(issue)) return;
  usePaymentRecoveryIssues.setState({
    issues: [...state.issues.filter((entry) => entry.id !== issue.id), issue],
  });
  persistRegistry();
}

export function clearPaymentRecoveryIssue(id: string): void {
  const state = usePaymentRecoveryIssues.getState();
  if (!state.issues.some((entry) => entry.id === id)) return;
  // Recovery can briefly clear and then re-report the same condition. Keep its
  // dismissal so a background retry cannot resurrect a notice the user closed.
  usePaymentRecoveryIssues.setState({
    issues: state.issues.filter((entry) => entry.id !== id),
  });
  persistRegistry();
}

export function dismissPaymentRecoveryIssue(issue: PaymentRecoveryIssue): void {
  usePaymentRecoveryIssues.setState((state) => ({
    dismissed: {
      ...state.dismissed,
      [issue.id]:
        state.issues.find((current) => current.id === issue.id)?.revision ?? issue.revision,
    },
  }));
  persistRegistry();
}

/** Clear the current notice group in memory before attempting best-effort persistence. */
export function dismissPaymentRecoveryNotices(businessId: string): void {
  usePaymentRecoveryIssues.setState((state) => {
    const dismissed = { ...state.dismissed };
    state.issues.forEach((issue) => {
      if (!issue.businessId || issue.businessId === businessId)
        dismissed[issue.id] = issue.revision;
    });
    return { dismissed };
  });
  persistRegistry();
}
